import {
  Calculator,
  FileSignature,
  Folder,
  FolderOpen,
  Images,
  KeyRound,
  Landmark,
  Package,
  Receipt,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { PropertyDocumentKind } from '@/shared/api';

/**
 * Wohin ein Drop / Upload-Klick auf die Kategorie geht:
 *   - direct:    keine Pflicht-Metadaten, alle Dateien sofort parallel hochladen
 *   - contracts: Vertraege via ContractsUploadModal (eigene Metadaten)
 *   - invoices:  Rechnungen via SupplierInvoicesUploadModal (eigene Metadaten)
 *   - generic:   Abrechnung/Steuer/Wartung via generisches Multi-Modal (issuedOn required)
 */
export type UploadRoute = 'direct' | 'contracts' | 'invoices' | 'generic';

/**
 * Die zehn Top-Kategorien der Akte: Speicher-Pfad, fachlicher Name, was hineingehört, welches
 * Symbol sie trägt und über welchen Upload-Weg Dateien dort landen.
 */
export interface TopFolder {
  /** Der echte Ordner im Storage (`PropertyFilePathBuilder`) — nie Anzeigetext. */
  folderPath: string;
  title: string;
  /** Kurzbeschreibung unter dem Titel: was gehört hier hinein? */
  description: string;
  defaultKind: PropertyDocumentKind;
  icon: LucideIcon;
  upload: UploadRoute;
}

export const TOP_FOLDERS: TopFolder[] = [
  {
    folderPath: '01_Stammakte',
    title: 'Stammakte',
    description: 'Pass, Grundriss, Schlüsselplan',
    defaultKind: 'Stammakte',
    icon: Folder,
    upload: 'direct',
  },
  {
    folderPath: '02_Vertraege',
    title: 'Verträge',
    description: 'Mietvertrag, Strom, Internet, Versicherung',
    defaultKind: 'Vertrag',
    icon: FileSignature,
    upload: 'contracts',
  },
  {
    folderPath: '03_Rechnungen',
    title: 'Rechnungen',
    description: 'Strom, Gas, Wasser, Internet, Reparatur',
    defaultKind: 'Rechnung',
    icon: Receipt,
    upload: 'invoices',
  },
  {
    folderPath: '04_Abrechnungen',
    title: 'Abrechnungen',
    description: 'Jahresabrechnungen — Nebenkosten, Strom',
    defaultKind: 'Abrechnung',
    icon: Calculator,
    upload: 'generic',
  },
  {
    folderPath: '05_Steuer-Behoerde',
    title: 'Steuer & Behörde',
    description: 'Steuerunterlagen + Behördenschreiben',
    defaultKind: 'SteuerLaufend',
    icon: Landmark,
    upload: 'generic',
  },
  {
    folderPath: '06_Wartung-Reparatur',
    title: 'Wartung & Reparatur',
    description: 'Wartung, Reparatur, Service-Belege',
    defaultKind: 'WartungReparatur',
    icon: Wrench,
    upload: 'generic',
  },
  {
    folderPath: '07_Inventar',
    title: 'Inventar',
    description: 'Inventar-Liste, Garantien, Anleitungen',
    defaultKind: 'InventarListe',
    icon: Package,
    upload: 'direct',
  },
  {
    folderPath: '08_Fotos',
    title: 'Fotos',
    description: 'Marketing, Zustand, Inventar, Schaden, Reinigung',
    defaultKind: 'FotoMarketing',
    icon: Images,
    upload: 'generic',
  },
  {
    folderPath: '09_Schluessel-Zugang',
    title: 'Schlüssel & Zugang',
    description: 'Schlüssel-Liste, Code-Liste, Zugangsdaten',
    defaultKind: 'SchluesselZugang',
    icon: KeyRound,
    upload: 'direct',
  },
  {
    folderPath: '99_Sonstiges',
    title: 'Sonstiges',
    description: 'Alles was nicht in andere Kategorien passt',
    defaultKind: 'Sonstiges',
    icon: FolderOpen,
    upload: 'direct',
  },
];

/** Die TopFolder-Kategorie zu einem Ordner-Pfad (erstes Segment) — Fallback: Sonstiges. */
export function topFolderForPath(path: string): TopFolder {
  const category = path.split('/').filter(Boolean)[0];
  return TOP_FOLDERS.find((m) => m.folderPath === category) ?? TOP_FOLDERS[TOP_FOLDERS.length - 1]!;
}

/** Die Kurzbeschreibung einer Top-Kategorie, sonst null (Unterordner tragen keine). */
export function topFolderDescription(path: string): string | null {
  return TOP_FOLDERS.find((m) => m.folderPath === path)?.description ?? null;
}
